import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

class CartDrawerComponent extends DialogComponent {

  connectedCallback() {
    super.connectedCallback();
    /** @type {AbortController | null} */
    this._updateController = null;
    this.#cacheA11yElements();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:update', this.#handleCartUpdate);
    document.addEventListener('cart:refresh', this.#handleCartUpdate);
    document.addEventListener('cart:error', this.#handleCartError);

    const dismissBtn = this.querySelector('.cart-drawer__error-dismiss');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => this.#hideError());
    }

    this.#ensureCheckoutTargetTop();
    this.#initRecommendationsNav();
  }

  disconnectedCallback() {
    if (this._updateController) {
      this._updateController.abort();
      this._updateController = null;
    }
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    document.removeEventListener('cart:refresh', this.#handleCartUpdate);
    document.removeEventListener('cart:error', this.#handleCartError);
    if (this._recsObserver) {
      this._recsObserver.disconnect();
      this._recsObserver = null;
    }
    super.disconnectedCallback();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  /** @param {CustomEvent<{sections?: Record<string, string>}> | Event} event */
  #handleCartUpdate = async (event) => {
    try {
      // Cancel any in-flight update
      if (this._updateController) {
        this._updateController.abort();
      }
      this._updateController = new AbortController();
      const signal = this._updateController.signal;

      this.#hideError();

      const sectionEl = /** @type {HTMLElement | null} */ (this.querySelector('cart-items-component'));
      const sectionId = sectionEl && sectionEl.dataset ? sectionEl.dataset.sectionId : null;
      /** @type {Record<string, string> | null} */
      let sectionsMap = null;
      if (event && typeof event === 'object' && 'detail' in event) {
        const ce = /** @type {CustomEvent<{sections?: Record<string, string>}>} */ (event);
        if (ce.detail && ce.detail.sections) {
          sectionsMap = ce.detail.sections;
        }
      }

      if (!sectionsMap && sectionId) {
        const url = new URL('/cart', window.location.origin);
        url.searchParams.set('sections', sectionId);
        const res = await fetch(url.toString(), { headers: { Accept: 'application/json' }, signal });
        if (res.ok) {
          const json = await res.json();
          if (json && json[sectionId]) {
            sectionsMap = json;
          }
        }
      }

      let doc;
      if (sectionsMap && sectionId && sectionsMap[sectionId]) {
        const html = sectionsMap[sectionId];
        const parser = new DOMParser();
        doc = parser.parseFromString(html, 'text/html');
      } else {
        const response = await fetch('/cart', { headers: { Accept: 'text/html' }, signal });
        if (!response.ok) {
          throw new Error('Cart HTML fetch failed');
        }
        const html = await response.text();
        const parser = new DOMParser();
        doc = parser.parseFromString(html, 'text/html');
      }


      const newCartItems = doc.querySelector('.cart-drawer__items');
      const currentCartItems = this.querySelector('.cart-drawer__items');
      if (newCartItems && currentCartItems) {
        currentCartItems.innerHTML = newCartItems.innerHTML;
      }

      const newRecommendations = doc.querySelector('cart-recommendations');
      const currentRecommendations = this.querySelector('cart-recommendations');
      if (newRecommendations && currentRecommendations) {
        currentRecommendations.innerHTML = newRecommendations.innerHTML;
        if ('setupEventListeners' in currentRecommendations) {
          const setupListeners = currentRecommendations.setupEventListeners;
          if (typeof setupListeners === 'function') {
            setupListeners.call(currentRecommendations);
          }
        }
        if ('updateNav' in currentRecommendations) {
          const updateNav = currentRecommendations.updateNav;
          if (typeof updateNav === 'function') {
            updateNav.call(currentRecommendations);
          }
        }
        const grid = currentRecommendations.querySelector('[data-recommendations-grid]');
        grid?.dispatchEvent(new Event('scroll'));
      }

      const newFreeShipping = doc.querySelector('.free-shipping-banner');
      const currentFreeShipping = this.querySelector('.free-shipping-banner');
      if (newFreeShipping && currentFreeShipping) {
        currentFreeShipping.outerHTML = newFreeShipping.outerHTML;
      } else if (newFreeShipping && !currentFreeShipping) {
        const summaryEl = this.querySelector('.cart-drawer__summary');
        if (summaryEl) {
          summaryEl.insertAdjacentHTML('beforebegin', newFreeShipping.outerHTML);
        }
      }

      const currentCartSummary = this.querySelector('.cart-drawer__summary');
      const newCartSummary = doc.querySelector('.cart-drawer__summary');
      if (newCartSummary && currentCartSummary) {
        currentCartSummary.innerHTML = newCartSummary.innerHTML;
      }


      this.#initRecommendationsNav();
      this.#ensureCheckoutTargetTop();

      this.#announceCartUpdate(doc);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error('[CartDrawer] Update failed:', error);
      this.#showError(error.message || 'Der Warenkorb konnte nicht aktualisiert werden.');
    }
  };

  #ensureCheckoutTargetTop() {
    if (!(window.Shopify && Shopify.designMode)) return;
    const root = this.querySelector('.cart-drawer__dialog') || this;
    root.querySelectorAll('form[action*="/cart"]').forEach((f) => {
      f.setAttribute('target', '_top');
    });
    root.querySelectorAll('a[href*="/checkout"]').forEach((a) => {
      a.setAttribute('target', '_top');
    });
    root.querySelectorAll('button[name="checkout"]').forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          const form = btn.closest('form');
          if (form) form.setAttribute('target', '_top');
        },
        { once: true }
      );
    });
  }

  /** @param {string} message */
  #showError(message) {
    const errorEl = this.querySelector('.cart-drawer__error');
    const messageEl = this.querySelector('.cart-drawer__error-message');
    if (errorEl && messageEl) {
      messageEl.textContent = message;
      errorEl.removeAttribute('hidden');
    }
  }

  #hideError() {
    const errorEl = this.querySelector('.cart-drawer__error');
    if (errorEl) {
      errorEl.setAttribute('hidden', '');
    }
  }

  /** @param {CustomEvent<{message?: string}>} event */
  #handleCartError = (event) => {
    const message = event?.detail?.message || 'Ein Fehler ist aufgetreten.';
    this.#showError(message);
  };

  /** @param {Document} doc */
  #announceCartUpdate(doc) {
    const liveRegion = this.querySelector('.cart-drawer__live-region');
    if (!liveRegion) return;

    const newTotal = doc.querySelector(
      '.cart-drawer__summary .totals .price, .cart-drawer__summary [data-cart-total]'
    );
    if (newTotal) {
      liveRegion.textContent = '';
      requestAnimationFrame(() => {
        liveRegion.textContent = `Warenkorb aktualisiert. Gesamtsumme: ${newTotal.textContent.trim()}`;
      });
    }
  }

  #initRecommendationsNav() {
    const recs = /** @type {HTMLElement | null} */ (this.querySelector('cart-recommendations'));
    if (!recs) return;
    /** @param {Document | ShadowRoot | HTMLElement} root */
    const force = (root) => {
      const nav = root.querySelector('[data-recs-nav], .recs-nav, .navigation, [data-nav], [role="navigation"]');
      if (nav instanceof HTMLElement) {
        nav.removeAttribute('hidden');
        nav.setAttribute('aria-hidden', 'false');
        if (nav.style && nav.style.display === 'none') nav.style.display = '';
        if (nav.style && nav.style.visibility === 'hidden') nav.style.visibility = '';
        if (nav.style && nav.style.opacity === '0') nav.style.opacity = '';
      }
    };
    const ensure = () => {
      if ('setupEventListeners' in recs && typeof recs.setupEventListeners === 'function') {
        recs.setupEventListeners();
      }
      if ('updateNav' in recs && typeof recs.updateNav === 'function') {
        recs.updateNav();
      }
      const grid = recs.querySelector('[data-recommendations-grid]');
      grid?.dispatchEvent(new Event('scroll'));
      force(recs);
      if (recs.shadowRoot) force(recs.shadowRoot);
    };
    if (this._recsObserver) {
      this._recsObserver.disconnect();
      this._recsObserver = null;
    }
    const start = () => {
      ensure();
      this._recsObserver = new MutationObserver(() => ensure());
      this._recsObserver.observe(recs, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'style', 'class', 'aria-hidden'] });
    };
    if (customElements.get('cart-recommendations')) {
      start();
    } else {
      customElements.whenDefined('cart-recommendations').then(start);
    }
  }

  #cacheA11yElements() {
    const trigger = this.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger instanceof HTMLElement) {
      this._trigger = trigger;
    }
  }

  open() {
    if (!this._trigger) {
      this.#cacheA11yElements();
    }
    const trigger = this._trigger;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }
    this.showDialog();

    this.#ensureCheckoutTargetTop();
    this.#initRecommendationsNav();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.close(), { once: true });
    });
  }

  close() {
    this.closeDialog();
    const trigger = this._trigger;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
